import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, User, MessageCircle, Bookmark, Calendar, Mail, Tag, Edit2, Check, X, Bell, BellRing } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, getAuthHeader, checkAuth } = useAuth();
  const [profile, setProfile] = useState(null);
  const [comments, setComments] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [activeTab, setActiveTab] = useState("comments");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editTag, setEditTag] = useState("");
  const [saving, setSaving] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState({ email_enabled: true, push_enabled: true });
  const [savingPrefs, setSavingPrefs] = useState(false);

  useEffect(() => {
    if (!user || user === false) { navigate("/"); return; }
    fetchProfile();
    fetchComments();
    fetchBookmarks();
    fetchNotifPrefs();
  }, [user]);

  const fetchProfile = async () => {
    try {
      const { data } = await axios.get(`${API}/profile`, { headers: getAuthHeader() });
      setProfile(data);
      setEditName(data.name || "");
      setEditTag(data.user_tag || "");
    } catch (err) { console.error("Failed to load profile:", err); }
    finally { setLoading(false); }
  };

  const fetchComments = async () => {
    try {
      const { data } = await axios.get(`${API}/profile/comments`, { headers: getAuthHeader() });
      setComments(data.comments);
    } catch (err) { console.error("Failed to load comments:", err); }
  };

  const fetchBookmarks = async () => {
    try {
      const { data } = await axios.get(`${API}/bookmarks`, { headers: getAuthHeader() });
      setBookmarks(data.bookmarks);
    } catch (err) { console.error("Failed to load bookmarks:", err); }
  };

  const fetchNotifPrefs = async () => {
    try {
      const { data } = await axios.get(`${API}/notifications/preferences`, { headers: getAuthHeader() });
      setNotifPrefs({
        email_enabled: data.email_enabled !== false,
        push_enabled: data.push_enabled !== false
      });
    } catch (err) { console.error("Failed to load notification prefs:", err); }
  };

  const updateNotifPref = async (key, value) => {
    const updated = { ...notifPrefs, [key]: value };
    setNotifPrefs(updated);
    setSavingPrefs(true);
    try {
      await axios.put(`${API}/notifications/preferences`, {
        ...updated,
        categories: [],
        frequency: "digest",
        in_app_enabled: true
      }, { headers: getAuthHeader() });
      toast.success("Notification preference updated!");
    } catch {
      setNotifPrefs(prev => ({ ...prev, [key]: !value }));
      toast.error("Failed to update preference");
    } finally { setSavingPrefs(false); }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/profile`, { name: editName, user_tag: editTag }, { headers: getAuthHeader() });
      setProfile(prev => ({ ...prev, ...data.user }));
      setEditing(false);
      toast.success("Profile updated!");
      checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    } finally { setSaving(false); }
  };

  const removeBookmark = async (articleUrl) => {
    try {
      await axios.delete(`${API}/bookmarks?article_url=${encodeURIComponent(articleUrl)}`, { headers: getAuthHeader() });
      setBookmarks(prev => prev.filter(b => b.article_url !== articleUrl));
      setProfile(prev => prev ? { ...prev, bookmark_count: prev.bookmark_count - 1 } : prev);
      toast.success("Bookmark removed");
    } catch { toast.error("Failed to remove bookmark"); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const formatRelative = (dateStr) => {
    const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return formatDate(dateStr);
  };

  if (!user || user === false) return null;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--color-bg)" }}>
      <div className="max-w-4xl mx-auto px-3 sm:px-6 py-6 sm:py-8">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 mb-6 transition-colors text-sm" style={{ color: "var(--color-text-dim)" }} data-testid="back-button">
          <ArrowLeft className="w-4 h-4" />
          Back to News
        </button>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: "var(--color-accent)", borderTopColor: "transparent" }}></div>
          </div>
        ) : profile && (
          <>
            {/* Profile Header */}
            <div className="rounded-xl p-6 sm:p-8 mb-6" style={{ backgroundColor: "var(--color-card)", border: "1px solid var(--color-border)" }} data-testid="profile-card">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center shrink-0 text-2xl sm:text-3xl font-black" style={{ backgroundColor: "var(--color-accent-glow)", color: "var(--color-accent)", border: "2px solid var(--color-accent)" }}>
                  {(profile.name || "U")[0].toUpperCase()}
                </div>
                <div className="flex-1 w-full">
                  {editing ? (
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs font-medium mb-1 block" style={{ color: "var(--color-text-dim)" }}>Name</label>
                        <Input value={editName} onChange={e => setEditName(e.target.value)} className="text-sm" style={{ backgroundColor: "var(--color-input-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }} data-testid="edit-name-input" />
                      </div>
                      <div>
                        <label className="text-xs font-medium mb-1 block" style={{ color: "var(--color-text-dim)" }}>User Tag</label>
                        <div className="flex items-center gap-1">
                          <span className="text-sm font-bold" style={{ color: "var(--color-accent)" }}>@</span>
                          <Input value={editTag} onChange={e => setEditTag(e.target.value)} className="text-sm" style={{ backgroundColor: "var(--color-input-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }} data-testid="edit-tag-input" />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={handleSaveProfile} disabled={saving} size="sm" className="text-white text-xs" style={{ backgroundColor: "var(--color-accent)" }} data-testid="save-profile-btn">
                          <Check className="w-3 h-3 mr-1" /> {saving ? "Saving..." : "Save"}
                        </Button>
                        <Button onClick={() => { setEditing(false); setEditName(profile.name); setEditTag(profile.user_tag); }} size="sm" variant="outline" className="text-xs" style={{ borderColor: "var(--color-border)", color: "var(--color-text-muted)" }}>
                          <X className="w-3 h-3 mr-1" /> Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-3 mb-1">
                        <h1 className="text-xl sm:text-2xl font-black" style={{ color: "var(--color-text)", fontFamily: "Cabinet Grotesk, sans-serif" }} data-testid="profile-name">{profile.name}</h1>
                        <button onClick={() => setEditing(true)} className="p-1.5 rounded-lg transition-colors" style={{ color: "var(--color-text-dim)" }} data-testid="edit-profile-btn">
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-sm font-semibold mb-2" style={{ color: "var(--color-accent)" }} data-testid="profile-tag">@{profile.user_tag || "user"}</p>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs" style={{ color: "var(--color-text-dim)" }}>
                        <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{profile.email}</span>
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Joined {formatDate(profile.created_at)}</span>
                        {profile.role === "admin" && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-red-500/20 text-red-400">Admin</span>}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3 mt-6">
                <div className="p-3 sm:p-4 rounded-lg text-center" style={{ backgroundColor: "var(--color-card-hover)" }}>
                  <p className="text-xl sm:text-2xl font-black" style={{ color: "var(--color-accent)" }} data-testid="comment-count">{profile.comment_count}</p>
                  <p className="text-xs font-medium" style={{ color: "var(--color-text-dim)" }}>Comments</p>
                </div>
                <div className="p-3 sm:p-4 rounded-lg text-center" style={{ backgroundColor: "var(--color-card-hover)" }}>
                  <p className="text-xl sm:text-2xl font-black" style={{ color: "var(--color-accent)" }} data-testid="bookmark-count">{profile.bookmark_count}</p>
                  <p className="text-xs font-medium" style={{ color: "var(--color-text-dim)" }}>Bookmarks</p>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 mb-4 flex-wrap">
              <button
                onClick={() => setActiveTab("comments")}
                className="flex items-center gap-2 px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-bold transition-all"
                style={activeTab === "comments" ? { backgroundColor: "var(--color-accent)", color: "#fff" } : { backgroundColor: "var(--color-card)", color: "var(--color-text-muted)" }}
                data-testid="tab-comments"
              >
                <MessageCircle className="w-4 h-4" />
                Comments ({profile.comment_count})
              </button>
              <button
                onClick={() => setActiveTab("bookmarks")}
                className="flex items-center gap-2 px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-bold transition-all"
                style={activeTab === "bookmarks" ? { backgroundColor: "var(--color-accent)", color: "#fff" } : { backgroundColor: "var(--color-card)", color: "var(--color-text-muted)" }}
                data-testid="tab-bookmarks"
              >
                <Bookmark className="w-4 h-4" />
                Bookmarks ({profile.bookmark_count})
              </button>
              <button
                onClick={() => setActiveTab("notifications")}
                className="flex items-center gap-2 px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-bold transition-all"
                style={activeTab === "notifications" ? { backgroundColor: "var(--color-accent)", color: "#fff" } : { backgroundColor: "var(--color-card)", color: "var(--color-text-muted)" }}
                data-testid="tab-notifications"
              >
                <Bell className="w-4 h-4" />
                Notifications
              </button>
            </div>

            {/* Tab Content */}
            <div className="rounded-xl overflow-hidden" style={{ backgroundColor: "var(--color-card)", border: "1px solid var(--color-border)" }}>
              {activeTab === "comments" && (
                comments.length === 0 ? (
                  <div className="py-12 text-center" data-testid="no-comments">
                    <MessageCircle className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--color-text-dim)" }} />
                    <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>No comments yet. Start joining the conversation!</p>
                  </div>
                ) : (
                  comments.map((comment, idx) => (
                    <div key={comment.id || comment.created_at || idx} className="p-4 sm:p-5" style={{ borderBottom: idx < comments.length - 1 ? "1px solid var(--color-border)" : "none" }} data-testid={`comment-item-${idx}`}>
                      <p className="text-sm leading-relaxed mb-2" style={{ color: "var(--color-text)" }}>{comment.text}</p>
                      <div className="flex flex-wrap items-center gap-2 text-[11px]" style={{ color: "var(--color-text-dim)" }}>
                        <span>{formatRelative(comment.created_at)}</span>
                        <span>•</span>
                        <a href={comment.article_url} target="_blank" rel="noopener noreferrer" className="truncate max-w-[200px] sm:max-w-[400px] hover:underline" style={{ color: "var(--color-accent)" }}>
                          {comment.article_url}
                        </a>
                        {comment.like_count > 0 && <span>• {comment.like_count} likes</span>}
                      </div>
                    </div>
                  ))
                )
              )}

              {activeTab === "bookmarks" && (
                bookmarks.length === 0 ? (
                  <div className="py-12 text-center" data-testid="no-bookmarks">
                    <Bookmark className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--color-text-dim)" }} />
                    <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>No bookmarks yet. Save articles to read later!</p>
                  </div>
                ) : (
                  bookmarks.map((bookmark, idx) => (
                    <div key={bookmark.article_url || idx} className="p-4 sm:p-5 flex items-start gap-3 sm:gap-4" style={{ borderBottom: idx < bookmarks.length - 1 ? "1px solid var(--color-border)" : "none" }} data-testid={`bookmark-item-${idx}`}>
                      {bookmark.article_image && (
                        <img src={bookmark.article_image} alt="" className="w-16 sm:w-20 h-12 sm:h-14 rounded-lg object-cover shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <a href={bookmark.article_url} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold leading-snug hover:underline line-clamp-2" style={{ color: "var(--color-text)", fontFamily: "Cabinet Grotesk, sans-serif" }}>
                          {bookmark.article_title}
                        </a>
                        <div className="flex items-center gap-2 text-[11px] mt-1" style={{ color: "var(--color-text-dim)" }}>
                          {bookmark.article_source && <span className="font-semibold" style={{ color: "var(--color-live)" }}>{bookmark.article_source}</span>}
                          {bookmark.article_category && <><span>•</span><span className="uppercase">{bookmark.article_category}</span></>}
                          <span>•</span>
                          <span>Saved {formatRelative(bookmark.created_at)}</span>
                        </div>
                      </div>
                      <button onClick={() => removeBookmark(bookmark.article_url)} className="p-1.5 rounded-lg shrink-0 transition-colors" style={{ color: "var(--color-text-dim)" }} data-testid={`remove-bookmark-${idx}`}>
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                )
              )}

              {activeTab === "notifications" && (
                <div className="p-5 sm:p-6 space-y-6" data-testid="notification-settings">
                  <div>
                    <h3 className="text-base font-bold mb-1" style={{ color: "var(--color-text)", fontFamily: "Cabinet Grotesk, sans-serif" }}>Breaking News Delivery</h3>
                    <p className="text-xs mb-5" style={{ color: "var(--color-text-dim)" }}>
                      Breaking news digests are delivered twice daily at <strong style={{ color: "var(--color-accent)" }}>7:00 AM</strong> and <strong style={{ color: "var(--color-accent)" }}>7:00 PM</strong> your local time. Choose how you'd like to receive them:
                    </p>
                  </div>

                  {/* Email toggle */}
                  <div className="flex items-center justify-between p-4 rounded-lg" style={{ backgroundColor: "var(--color-card-hover)" }}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: "var(--color-accent-glow)" }}>
                        <Mail className="w-5 h-5" style={{ color: "var(--color-accent)" }} />
                      </div>
                      <div>
                        <p className="text-sm font-bold" style={{ color: "var(--color-text)" }}>Email Notifications</p>
                        <p className="text-xs" style={{ color: "var(--color-text-dim)" }}>Receive digest emails with all breaking stories</p>
                      </div>
                    </div>
                    <button
                      onClick={() => updateNotifPref("email_enabled", !notifPrefs.email_enabled)}
                      disabled={savingPrefs}
                      className="relative w-12 h-7 rounded-full transition-colors shrink-0"
                      style={{ backgroundColor: notifPrefs.email_enabled ? "var(--color-accent)" : "var(--color-border)" }}
                      data-testid="toggle-email-notif"
                    >
                      <span className="absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform"
                        style={{ transform: notifPrefs.email_enabled ? "translateX(20px)" : "translateX(0)" }} />
                    </button>
                  </div>

                  {/* Push toggle */}
                  <div className="flex items-center justify-between p-4 rounded-lg" style={{ backgroundColor: "var(--color-card-hover)" }}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: "var(--color-accent-glow)" }}>
                        <BellRing className="w-5 h-5" style={{ color: "var(--color-accent)" }} />
                      </div>
                      <div>
                        <p className="text-sm font-bold" style={{ color: "var(--color-text)" }}>Push Notifications</p>
                        <p className="text-xs" style={{ color: "var(--color-text-dim)" }}>Receive browser push alerts with breaking news</p>
                      </div>
                    </div>
                    <button
                      onClick={() => updateNotifPref("push_enabled", !notifPrefs.push_enabled)}
                      disabled={savingPrefs}
                      className="relative w-12 h-7 rounded-full transition-colors shrink-0"
                      style={{ backgroundColor: notifPrefs.push_enabled ? "var(--color-accent)" : "var(--color-border)" }}
                      data-testid="toggle-push-notif"
                    >
                      <span className="absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform"
                        style={{ transform: notifPrefs.push_enabled ? "translateX(20px)" : "translateX(0)" }} />
                    </button>
                  </div>

                  <div className="p-4 rounded-lg" style={{ backgroundColor: "var(--color-accent-glow)", border: "1px solid var(--color-accent)" }}>
                    <p className="text-xs leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
                      Your timezone is automatically detected from your browser. All breaking news stories are collected throughout the day and delivered as a digest at 7 AM and 7 PM your local time.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
